/**
 * Shared client predicate — kept in lockstep with the server-side
 * version in SsoService. A transaction counts as a "paid entry fee"
 * only when ALL of:
 *
 *   • fee === "AAEventFee"
 *   • voided !== true
 *   • value > 0 AND not the kupartybus presence stub OR
 *     value === 0 AND source ∈ staff-confirmed list (comp) OR
 *     value === 0 AND overrideAmount === 0 with a reason (modal comp)
 *
 * Specifically NOT paid:
 *   • the kupartybus auto-stub (value=0, source="kupartybus_sso_presence_stub")
 *   • voided transactions (reset / edited-from)
 *   • untagged $0 transactions (legacy stubs from before the source tag)
 */
const STAFF_CONFIRMED_SOURCES = new Set([
    'sso_roster_entry_fee_modal',
    'sso_roster_entry_fee_edit',
    'scanner_all_access_checkin',
    'pink_fee_checkin',
]);

const isRealPaidEntryFeeTx = (tx: any): boolean => {
    if (!tx) return false;
    if (tx.fee !== 'AAEventFee') return false;
    if (tx.voided === true) return false;
    const value = Number(tx.value) || 0;
    const source = String(tx.source || '');
    if (value > 0) return source !== 'kupartybus_sso_presence_stub';
    if (value === 0 && STAFF_CONFIRMED_SOURCES.has(source)) return true;
    if (
        value === 0 &&
        Number(tx.overrideAmount) === 0 &&
        typeof tx.overrideReason === 'string' &&
        tx.overrideReason.trim().length > 0
    ) {
        return true;
    }
    return false;
};

/**
 * Convenience predicate: is this an SSO presence-only stub?
 * Used to label rows on the transaction page as "Pending Entry Fee"
 * rather than $0 paid.
 */
export const isSsoPresenceStub = (tx: any): boolean => {
    if (!tx) return false;
    if (tx.fee !== 'AAEventFee') return false;
    if (tx.voided === true) return false;
    const value = Number(tx.value) || 0;
    if (value !== 0) return false;
    const source = String(tx.source || '');
    if (source === 'kupartybus_sso_presence_stub') return true;
    // Legacy untagged $0 stub (pre-source-tagging) — still treat as
    // pending so the transaction page doesn't claim it's paid.
    if (
        source === '' &&
        !(
            Number(tx.overrideAmount) === 0 &&
            typeof tx.overrideReason === 'string' &&
            tx.overrideReason.trim().length > 0
        )
    ) {
        return true;
    }
    return false;
};

export default isRealPaidEntryFeeTx;
